import axios from 'axios';

// 创建axios实例
const configuredBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').trim();
const inferredBaseUrl = (() => {
  try {
    const origin = window.location.origin;
    return `${origin}/api/v1`;
  } catch (e) {
    return 'http://localhost:8000/api/v1';
  }
})();

const api = axios.create({
  baseURL: configuredBaseUrl || inferredBaseUrl,
  timeout: 10000,
});

// 请求拦截器：添加认证token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器：处理401错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token失效，清除本地存储并跳转到登录页
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 认证相关API
export const authAPI = {
  login: (username, password) =>
    api.post(
      '/auth/login',
      new URLSearchParams({
        username,
        password,
      }),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    ),

  register: (userData) => api.post('/auth/register', userData),

  me: () => api.get('/auth/me'),
  pendingUsers: () => api.get('/auth/pending-users'),
  approveUser: (userId) => api.post(`/auth/users/${userId}/approve`),

  roles: () => api.get('/auth/roles'),
  users: () => api.get('/auth/users'),
  setUserRole: (userId, roleName) => api.post(`/auth/users/${userId}/role`, { role_name: roleName }),

  changePassword: (oldPassword, newPassword, logoutOthers = true) =>
    api.post('/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
      logout_others: logoutOthers,
    }),
};

// 交易相关API
export const tradeAPI = {
  getDataSources: () => api.get('/trade/data-sources'),

  createDataSource: (dataSourceData) => api.post('/trade/data-sources', dataSourceData),

  updateDataSource: (dataSourceId, dataSourceData) => api.put(`/trade/data-sources/${dataSourceId}`, dataSourceData),

  deleteDataSource: (dataSourceId) => api.delete(`/trade/data-sources/${dataSourceId}`),

  // 数据源认证方法API
  getAuthMethods: () => api.get('/trade/auth-methods'),

  createAuthMethod: (authMethodData) => api.post('/trade/auth-methods', authMethodData),

  updateAuthMethod: (authMethodId, authMethodData) => api.put(`/trade/auth-methods/${authMethodId}`, authMethodData),

  deleteAuthMethod: (authMethodId) => api.delete(`/trade/auth-methods/${authMethodId}`),

  // 交易员资料API
  getTraderProfiles: () => api.get('/trade/trader-profiles'),

  createTraderProfile: (traderProfileData) => api.post('/trade/trader-profiles', traderProfileData),

  updateTraderProfile: (traderProfileId, traderProfileData) => api.put(`/trade/trader-profiles/${traderProfileId}`, traderProfileData),

  deleteTraderProfile: (traderProfileId) => api.delete(`/trade/trader-profiles/${traderProfileId}`),

  getTopics: () => api.get('/trade/topics'),

  createTopic: (topicData) => api.post('/trade/topics', topicData),

  updateTopic: (topicId, topicData) => api.put(`/trade/topics/${topicId}`, topicData),

  deleteTopic: (topicId) => api.delete(`/trade/topics/${topicId}`),

  updateTopicSchedule: (topicId, schedule) => api.put(`/trade/topics/${topicId}/schedule`, schedule),

  manualSyncTopic: (topicId) => api.post(`/trade/topics/${topicId}/manual-sync`),

  getTradeRecords: (topicId = null) =>
    api.get('/trade/trade-records', {
      params: topicId ? { topic_id: topicId } : {},
    }),

  getLiveFollows: () => api.get('/trade/live-follows'),

  // 交易话题分表API
  getTopicTradeTables: () => api.get('/trade/topic-trade-tables'),

  createTopicTradeTable: (tableData) => api.post('/trade/topic-trade-tables', tableData),

  deleteTopicTradeTable: (topicId) => api.delete(`/trade/topic-trade-tables/${topicId}`),
};

// 通知相关API
export const notificationAPI = {
  getWebhooks: () => api.get('/notification/webhooks'),

  createWebhook: (webhookData) => api.post('/notification/webhooks', webhookData),

  getWebhook: (webhookId) => api.get(`/notification/webhooks/${webhookId}`),

  updateWebhook: (webhookId, webhookData) => api.put(`/notification/webhooks/${webhookId}`, webhookData),

  deleteWebhook: (webhookId) => api.delete(`/notification/webhooks/${webhookId}`),

  testWebhook: (webhookId) => api.post(`/notification/webhooks/${webhookId}/test`),

  getTopicWebhooks: (topicId) => api.get(`/notification/topic-webhooks/${topicId}`),

  updateTopicWebhooks: (topicId, bindings) => api.put(`/notification/topic-webhooks/${topicId}`, { bindings }),

  getWebhookOccupancy: () => api.get('/notification/topic-webhooks/occupancy'),
};

// 券商账户API
export const brokerAPI = {
  list: (broker) => api.get(`/broker-accounts/${broker}/`),
  create: (broker, data) => api.post(`/broker-accounts/${broker}/`, data),
  get: (broker, id) => api.get(`/broker-accounts/${broker}/${id}`),
  update: (broker, id, data) => api.put(`/broker-accounts/${broker}/${id}`, data),
  delete: (broker, id) => api.delete(`/broker-accounts/${broker}/${id}`),
  test: (broker, id) => api.post(`/broker-accounts/${broker}/${id}/test`),
  refreshLongPortToken: (id, expiredDays = 90) =>
    api.post(`/broker-accounts/long_port/${id}/refresh-access-token`, null, {
      params: { expired_days: expiredDays },
    }),
};

// 实盘跟随（live_follow）API
export const liveFollowAPI = {
  list: () => api.get('/live-follow/'),
  create: (data) => api.post('/live-follow/', data),
  get: (id) => api.get(`/live-follow/${id}`),
  update: (id, data) => api.put(`/live-follow/${id}`, data),
  delete: (id) => api.delete(`/live-follow/${id}`),
  startSync: (id) => api.post(`/live-follow/${id}/start-sync`),
  startNotify: (id) => api.post(`/live-follow/${id}/start-notify`),
  stopSync: (id) => api.post(`/live-follow/${id}/stop-sync`),
  stopNotify: (id) => api.post(`/live-follow/${id}/stop-notify`),
  getOperationLogs: (followId, params = {}) => api.get(`/live-follow/${followId}/operation-logs`, { params }),
  listOperationLogs: (params = {}) => api.get('/live-follow/operations/logs', { params }),
  listIgnoredSymbols: (params = {}) => api.get('/live-follow/config/ignore-symbols', { params }),
  createIgnoredSymbol: (data) => api.post('/live-follow/config/ignore-symbols', data),
  updateIgnoredSymbol: (id, data) => api.put(`/live-follow/config/ignore-symbols/${id}`, data),
  deleteIgnoredSymbol: (id) => api.delete(`/live-follow/config/ignore-symbols/${id}`),
  getJobRecords: (followId, limit = 100, offset = 0) =>
    api.get('/trade/live-follow-job-records', {
      params: { follow_id: followId, limit, offset },
    }),
};

export default api;
